import type { PlaidLinkOnEventMetadata } from 'react-plaid-link';
import { useState } from 'react';
import { List, ListItem, ListItemContent, ListItemText } from '@savvy-kit/components/list';
import { Text } from '@savvy-kit/foundations/text';
import { ChevronRight, Pencil } from 'lucide-react';
import { AggregatorConnectButton } from 'src/components/aggregator/aggregator-connect-button';
import { ItemType } from 'src/constants/account-link';
import { PlaidErrorCode } from 'src/types/plaid';

import type { PlaidProduct } from '@savvy/constants/generated/gql-enums';
import { PLAID_PRODUCT_INVESTMENTS, PLAID_PRODUCT_TRANSACTIONS } from '@savvy/constants/plaid-products';

import type { AddAccountItemType } from '../constants';
import { AddAccountAccountForm } from './add-account-account-form';
import { AddAccountAssetForm } from './add-account-asset-form';
import { AddAccountCompanyEquityForm } from './add-account-company-equity-form';
import {
  ACCOUNT_FORM_TITLES,
  CONNECT_CARD_FEED_ONLY_NOTE,
  CONNECT_FEED_DESCRIPTION,
  CONNECT_FEED_TITLE,
  CONNECT_MANUAL_DESCRIPTION,
  CONNECT_MANUAL_LABEL,
  mfaFallbackNote,
  MORTGAGE_ADD_LABEL,
  MORTGAGE_SKIP_LABEL,
  MORTGAGE_STEP_TITLE,
} from './add-account-constants';
import { AddAccountFormShell } from './add-account-form-shell';
import { AddAccountLiabilityForm } from './add-account-liability-form';

const FEED_LINKED_TYPES: readonly AddAccountItemType[] = [ItemType.BANK_ACCOUNT, ItemType.INVESTMENT_ACCOUNT];

function feedTitleFor({ feedOnly, isInvestment }: { readonly feedOnly: boolean; readonly isInvestment: boolean }) {
  if (feedOnly) {
    return ACCOUNT_FORM_TITLES.card;
  }
  return isInvestment ? ACCOUNT_FORM_TITLES.investment : ACCOUNT_FORM_TITLES.bank;
}

function plaidProducts(itemType: AddAccountItemType): PlaidProduct[] {
  return itemType === ItemType.INVESTMENT_ACCOUNT
    ? [PLAID_PRODUCT_INVESTMENTS, PLAID_PRODUCT_TRANSACTIONS]
    : [PLAID_PRODUCT_TRANSACTIONS];
}

type AddAccountFlowProps = {
  readonly itemType: AddAccountItemType;
  readonly feedOnly?: boolean;
  readonly onCancel: () => void;
  readonly onWriteLanded?: () => void;
  readonly onLinked: () => Promise<unknown>;
};

export function AddAccountFlow({ itemType, feedOnly = false, onCancel, onWriteLanded, onLinked }: AddAccountFlowProps) {
  const [enteringManually, setEnteringManually] = useState(!feedOnly && !FEED_LINKED_TYPES.includes(itemType));
  const [manualEntryReason, setManualEntryReason] = useState<string | undefined>(undefined);
  const [mortgageAssetId, setMortgageAssetId] = useState<string | undefined>(undefined);
  const [mortgageAnswered, setMortgageAnswered] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const finishWithoutMortgage = () => {
    void onLinked();
  };

  if (!enteringManually) {
    const isInvestment = itemType === ItemType.INVESTMENT_ACCOUNT;
    const feedTitle = feedTitleFor({ feedOnly, isInvestment });
    return (
      <AddAccountFormShell title={feedTitle} onCancel={onCancel}>
        <div className="bg-dark text-light rounded-md">
          <AggregatorConnectButton
            products={plaidProducts(itemType)}
            title={CONNECT_FEED_TITLE}
            subtitle={CONNECT_FEED_DESCRIPTION}
            onSuccess={onLinked}
            setIsLoading={setConnecting}
            onPlaidError={(metadata: PlaidLinkOnEventMetadata) => {
              if (metadata.error_code !== PlaidErrorCode.MFA_NOT_SUPPORTED || feedOnly) {
                return;
              }
              setManualEntryReason(mfaFallbackNote(metadata.institution_name));
              setEnteringManually(true);
            }}
          />
        </div>
        {feedOnly && <Text variant="body-sm">{CONNECT_CARD_FEED_ONLY_NOTE}</Text>}
        {!feedOnly && (
          <List>
            <ListItem className="p-0">
              <button
                type="button"
                className="hover:bg-component-hover flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left"
                disabled={connecting}
                onClick={() => {
                  setEnteringManually(true);
                }}
              >
                <Pencil className="text-dark size-5 shrink-0" aria-hidden="true" />
                <ListItemContent>
                  <ListItemText primary>{CONNECT_MANUAL_LABEL}</ListItemText>
                  <ListItemText secondary>{CONNECT_MANUAL_DESCRIPTION}</ListItemText>
                </ListItemContent>
                <ChevronRight className="text-secondary size-5 shrink-0" aria-hidden="true" />
              </button>
            </ListItem>
          </List>
        )}
      </AddAccountFormShell>
    );
  }

  if (itemType === ItemType.BANK_ACCOUNT || itemType === ItemType.INVESTMENT_ACCOUNT) {
    return (
      <AddAccountAccountForm
        accountType={itemType === ItemType.INVESTMENT_ACCOUNT ? 'investment' : 'depository'}
        manualEntryReason={manualEntryReason}
        onCancel={onCancel}
        onCreated={onLinked}
      />
    );
  }

  if (itemType === ItemType.COMPANY_EQUITY) {
    return <AddAccountCompanyEquityForm onCancel={onCancel} onCreated={onLinked} />;
  }

  if (itemType === ItemType.OTHER_LIABILITY) {
    return <AddAccountLiabilityForm onCancel={onCancel} onCreated={onLinked} submitLabel="Add liability" />;
  }

  if (itemType === ItemType.OTHER_ASSET) {
    return <AddAccountAssetForm realEstate={false} onCancel={onCancel} onCreated={onLinked} submitLabel="Add asset" />;
  }

  if (mortgageAssetId === undefined) {
    return (
      <AddAccountAssetForm
        realEstate
        onCancel={onCancel}
        onCreated={(assetId) => {
          setMortgageAssetId(assetId);
          onWriteLanded?.();
          return Promise.resolve();
        }}
        submitLabel="Add property"
      />
    );
  }

  if (!mortgageAnswered) {
    return (
      <AddAccountFormShell
        title={MORTGAGE_STEP_TITLE}
        onCancel={finishWithoutMortgage}
        cancelLabel={MORTGAGE_SKIP_LABEL}
        onSubmit={() => {
          setMortgageAnswered(true);
        }}
        submitLabel={MORTGAGE_ADD_LABEL}
      >
        <Text variant="body-sm">
          A mortgage is subtracted from your net worth, so adding it keeps the property from counting twice.
        </Text>
      </AddAccountFormShell>
    );
  }

  return (
    <AddAccountLiabilityForm
      onCancel={finishWithoutMortgage}
      cancelLabel={MORTGAGE_SKIP_LABEL}
      onStepBack={() => {
        setMortgageAnswered(false);
      }}
      onCreated={onLinked}
      mortgageForAssetId={mortgageAssetId}
      submitLabel="Add mortgage"
    />
  );
}
